import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import CollectionTypeSelector from './components/CollectionTypeSelector';
import CSVImport from './components/CSVImport';
import { CollectionsService } from '../../services/collectionsService';
import { CollectiblesService } from '../../services/collectiblesService';

const AddCollection = () => {
  const navigate = useNavigate();
  const { user, loading: authLoading } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [showCSVImport, setShowCSVImport] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Redirect unauthenticated users
  useEffect(() => {
    if (!authLoading && !user) {
      navigate('/login', { replace: true });
    }
  }, [user, authLoading, navigate]);

  // Form state
  const [collectionData, setCollectionData] = useState({
    name: '',
    description: '',
    collectible_type: '',
    is_public: false
  });

  const steps = [
    { id: 1, name: 'Category', description: 'Select collection type', icon: 'Package' },
    { id: 2, name: 'Details', description: 'Collection information', icon: 'FileText' },
    { id: 3, name: 'Items', description: 'Add initial items', icon: 'Plus' }
  ];

  const handleNext = () => {
    if (currentStep < steps?.length) {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleInputChange = (field, value) => {
    setCollectionData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleCreateCollection = async () => {
    if (!user) {
      navigate('/login', { replace: true });
      return;
    }

    setLoading(true);
    setError('');

    try {
      const { data: newCollection, error: createError } = await CollectionsService?.createCollection({
        user_id: user?.id,
        name: collectionData?.name,
        description: collectionData?.description,
        collectible_type: collectionData?.collectible_type,
        is_public: collectionData?.is_public
      });

      if (createError) {
        throw createError;
      }

      // Navigate to collection detail page
      navigate(`/collection-detail?id=${newCollection?.id}`, {
        state: { 
          message: 'Collection created successfully!',
          newCollection
        }
      });

    } catch (err) {
      setError(err?.message || 'Failed to create collection. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCSVImport = async (collectionId, importedItems) => {
    try {
      const results = await Promise.all(
        importedItems?.map(itemData => 
          CollectiblesService?.createCollectible({
            ...itemData,
            user_id: user?.id,
            collection_id: collectionId,
            collectible_type: collectionData?.collectible_type
          })
        )
      );

      const successCount = results?.filter(result => !result?.error)?.length;
      
      navigate(`/collection-detail?id=${collectionId}`, {
        state: { 
          message: `Collection created with ${successCount} items imported successfully!`
        }
      });

    } catch (err) {
      setError(err?.message || 'Failed to import items');
    }
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1: return collectionData?.collectible_type !== '';
      case 2: return collectionData?.name?.trim() !== '';
      case 3: return true;
      default: return false;
    }
  };

  const getCollectionTypeDisplay = (type) => {
    return type?.split('-')?.map(word => word?.charAt(0)?.toUpperCase() + word?.slice(1))?.join(' ') || '';
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                What Collectible Category is the Collection?
              </h3>
              <p className="text-sm text-muted-foreground">
                Choose the primary category for your collection. You can have multiple collections of the same category.
              </p>
            </div>
            
            <CollectionTypeSelector
              selectedType={collectionData?.collectible_type}
              onTypeChange={(type) => handleInputChange('collectible_type', type)}
            />
          </div>
        );
      
      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Collection Details
              </h3>
              <p className="text-sm text-muted-foreground">
                Give your collection a unique name and description to organize your {getCollectionTypeDisplay(collectionData?.collectible_type)} items.
              </p>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Collection Name *
                </label>
                <Input
                  value={collectionData?.name}
                  onChange={(e) => handleInputChange('name', e?.target?.value)}
                  placeholder={`My ${getCollectionTypeDisplay(collectionData?.collectible_type)} Collection`}
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-foreground mb-2">
                  Description
                </label>
                <textarea
                  value={collectionData?.description}
                  onChange={(e) => handleInputChange('description', e?.target?.value)}
                  placeholder="Describe your collection..."
                  rows={4}
                  className="w-full px-3 py-2 bg-background border border-border rounded-lg text-foreground focus:ring-2 focus:ring-primary focus:border-primary resize-none"
                />
              </div>

              <div className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  id="is_public"
                  checked={collectionData?.is_public}
                  onChange={(e) => handleInputChange('is_public', e?.target?.checked)}
                  className="w-4 h-4 text-primary bg-background border border-border rounded focus:ring-primary focus:ring-2"
                />
                <label htmlFor="is_public" className="text-sm font-medium text-foreground">
                  Make this collection public
                </label>
              </div>
              <p className="text-xs text-muted-foreground ml-7">
                Public collections can be viewed by other collectors in the community
              </p>
            </div>
            {/* Collection Preview */}
            <div className="bg-muted/20 border border-border rounded-lg p-4">
              <h4 className="font-medium text-foreground mb-3">Collection Preview</h4>
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Package" size={24} className="text-primary" />
                </div>
                <div className="flex-1 min-w-0">
                  <h5 className="font-semibold text-foreground">
                    {collectionData?.name || 'Collection Name'}
                  </h5>
                  <p className="text-sm text-muted-foreground capitalize">
                    {getCollectionTypeDisplay(collectionData?.collectible_type)} Collection
                  </p>
                  {collectionData?.description && (
                    <p className="text-sm text-muted-foreground mt-1">
                      {collectionData?.description}
                    </p>
                  )}
                  <div className="flex items-center space-x-4 mt-2 text-xs text-muted-foreground">
                    <span>0 items</span>
                    <span>•</span>
                    <span>{collectionData?.is_public ? 'Public' : 'Private'}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      
      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Add Initial Items
              </h3>
              <p className="text-sm text-muted-foreground">
                You can create an empty collection or import items from a CSV file to get started quickly.
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Create Empty Collection */}
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-muted/50 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Package" size={32} className="text-muted-foreground" />
                </div>
                <h4 className="font-semibold text-card-foreground mb-2">
                  Start Empty
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Create the collection now and add items later manually or through the collection detail page.
                </p>
                <Button
                  onClick={handleCreateCollection}
                  loading={loading}
                  disabled={loading}
                  iconName="Plus"
                  iconPosition="left"
                  className="w-full"
                >
                  {loading ? 'Creating...' : 'Create Empty Collection'}
                </Button>
              </div>

              {/* Import from CSV */}
              <div className="bg-card border border-border rounded-lg p-6 text-center">
                <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="Upload" size={32} className="text-primary" />
                </div>
                <h4 className="font-semibold text-card-foreground mb-2">
                  Import from CSV
                </h4>
                <p className="text-sm text-muted-foreground mb-4">
                  Upload a CSV file to bulk import multiple items into your new collection at once.
                </p>
                <Button
                  variant="outline"
                  onClick={() => setShowCSVImport(true)}
                  iconName="FileText"
                  iconPosition="left"
                  className="w-full"
                >
                  Import CSV File
                </Button>
              </div>
            </div>
            {/* CSV Template Download */}
            <div className="bg-muted/20 border border-border rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
                <div className="flex-1">
                  <h5 className="font-medium text-foreground mb-1">Need a CSV template?</h5>
                  <p className="text-sm text-muted-foreground mb-2">
                    Download our CSV template with the correct format and example data for {getCollectionTypeDisplay(collectionData?.collectible_type)} items.
                  </p>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      // Download CSV template
                      const headers = ['name', 'brand', 'model', 'year', 'condition', 'purchase_price', 'current_value', 'description', 'notes'];
                      const csvContent = [
                        headers?.join(','),
                        'Example Item,Example Brand,Model X,2020,mint,100,150,Item description,Optional notes'
                      ]?.join('\n');

                      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
                      const link = document.createElement('a');
                      const url = URL.createObjectURL(blob);
                      link?.setAttribute('href', url);
                      link?.setAttribute('download', `${collectionData?.collectible_type}_template.csv`);
                      link.style.visibility = 'hidden';
                      document.body?.appendChild(link);
                      link?.click();
                      document.body?.removeChild(link);
                    }}
                    iconName="Download"
                    iconPosition="left"
                  >
                    Download Template
                  </Button>
                </div>
              </div>
            </div>
          </div>
        );
      
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex pt-16">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        
        <main className={`flex-1 transition-all duration-300 ${sidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'}`}>
          <div className="p-6 lg:p-8">
            <div className="max-w-4xl mx-auto">
              {/* Header */}
              <div className="flex items-center justify-between mb-8">
                <div>
                  <h1 className="text-3xl font-bold text-foreground mb-2">
                    Add Collection
                  </h1>
                  <p className="text-muted-foreground">
                    Create a new collection to organize your collectibles by category and start building your portfolio.
                  </p>
                </div>
                
                <div className="flex space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => navigate('/collection-dashboard')}
                    iconName="ArrowLeft"
                    iconPosition="left"
                  >
                    Back to Dashboard
                  </Button>
                </div>
              </div>

              {/* Progress Steps */}
              <div className="mb-8">
                <div className="flex items-center justify-between">
                  {steps?.map((step, index) => (
                    <div key={step?.id} className="flex items-center">
                      <div className={`flex items-center space-x-3 ${index < steps?.length - 1 ? 'flex-1' : ''}`}>
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center border-2 transition-all duration-300 ${
                          currentStep === step?.id
                            ? 'bg-primary border-primary text-primary-foreground'
                            : currentStep > step?.id
                            ? 'bg-success border-success text-white' :'bg-background border-border text-muted-foreground'
                        }`}>
                          {currentStep > step?.id ? (
                            <Icon name="Check" size={16} />
                          ) : (
                            <Icon name={step?.icon} size={16} />
                          )}
                        </div>
                        
                        <div className="hidden md:block">
                          <p className={`text-sm font-medium ${
                            currentStep >= step?.id ? 'text-foreground' : 'text-muted-foreground'
                          }`}>
                            {step?.name}
                          </p>
                          <p className="text-xs text-muted-foreground">{step?.description}</p>
                        </div>
                      </div>
                      
                      {index < steps?.length - 1 && (
                        <div className={`hidden md:block flex-1 h-px mx-4 ${
                          currentStep > step?.id ? 'bg-success' : 'bg-border'
                        }`} />
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Step Content */}
              <div className="bg-card border border-border rounded-lg p-6 lg:p-8 mb-8">
                {error && (
                  <div className="mb-6 p-3 bg-destructive/10 border border-destructive/20 rounded-lg">
                    <div className="flex items-center space-x-2">
                      <Icon name="AlertCircle" size={16} className="text-destructive flex-shrink-0" />
                      <span className="text-sm text-destructive">{error}</span>
                    </div>
                  </div>
                )}
                
                {renderStepContent()}
              </div>

              {/* Navigation */}
              <div className="flex items-center justify-between">
                <div>
                  {currentStep > 1 && (
                    <Button
                      variant="outline"
                      onClick={handlePrevious}
                      iconName="ArrowLeft"
                      iconPosition="left"
                      disabled={loading}
                    >
                      Previous
                    </Button>
                  )}
                </div>
                
                <div className="flex space-x-3">
                  {currentStep < steps?.length ? (
                    <Button
                      onClick={handleNext}
                      disabled={!canProceed() || loading}
                      iconName="ArrowRight"
                      iconPosition="right"
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      onClick={handleCreateCollection}
                      loading={loading}
                      disabled={!canProceed() || loading}
                      iconName="Plus"
                      iconPosition="left"
                    >
                      {loading ? 'Creating...' : 'Create Collection'}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>

      {/* CSV Import Modal */}
      {showCSVImport && (
        <CSVImport
          collectionData={collectionData}
          onImport={async (items) => {
            // First create the collection
            const { data: newCollection, error: createError } = await CollectionsService?.createCollection({
              user_id: user?.id,
              name: collectionData?.name,
              description: collectionData?.description,
              collectible_type: collectionData?.collectible_type,
              is_public: collectionData?.is_public
            });

            if (createError) {
              throw createError;
            }

            // Then import the items
            await handleCSVImport(newCollection?.id, items);
            setShowCSVImport(false);
          }}
          onClose={() => setShowCSVImport(false)}
        />
      )}
    </div>
  );
};

export default AddCollection;